import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Reveal } from "./Reveal";
import { CheckCircle2, Minus, Plus, Loader2, ChevronDown } from "lucide-react";

const fmtDay = new Intl.DateTimeFormat("fr-FR", { weekday: "short" });
const fmtDate = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short" });
const fmtFull = new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long" });

export default function Booking({ content }) {
  const [availability, setAvailability] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [workshopId, setWorkshopId] = useState("");
  const [sessions, setSessions] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [seats, setSeats] = useState(2);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", referral_code: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    api.get("/sessions/availability").then((r) => {
      setAvailability(r.data);
      const first = r.data.find((d) => d.remaining > 0);
      if (first) setSelectedDate(first.date);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    setLoadingSlots(true);
    setSelectedSession(null);
    api.get(`/sessions?date=${selectedDate}`)
      .then((r) => {
        setSessions(r.data);
        const opts = [...new Map(r.data.map((s) => [s.workshop_id || "default", s.workshop_name])).entries()]
          .map(([id, name]) => ({ id, name }))
          .sort((a, b) => a.name.localeCompare(b.name, "fr"));
        setWorkshopId((current) => (opts.some((o) => o.id === current) ? current : opts[0]?.id || ""));
      })
      .catch(() => {})
      .finally(() => setLoadingSlots(false));
  }, [selectedDate]);

  const workshopOptions = useMemo(() => {
    const map = new Map();
    sessions.forEach((s) => {
      const key = s.workshop_id || "default";
      if (!map.has(key)) map.set(key, s.workshop_name);
    });
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "fr"));
  }, [sessions]);

  const visibleSessions = useMemo(() => {
    const seen = new Set();
    return sessions
      .filter((s) => (s.workshop_id || "default") === workshopId)
      .filter((s) => (seen.has(s.time) ? false : (seen.add(s.time), true)));
  }, [sessions, workshopId]);

  const maxSeats = useMemo(() => {
    if (!selectedSession) return 8;
    return Math.min(8, selectedSession.slot_remaining ?? selectedSession.remaining);
  }, [selectedSession]);

  useEffect(() => {
    setSeats((s) => Math.min(s, maxSeats || 1));
  }, [maxSeats]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/reservations", { session_id: selectedSession.id, seats, ...form });
      setConfirmation(data);
    } catch (err) {
      setError(apiError(err, "Impossible de finaliser la réservation"));
    } finally {
      setSubmitting(false);
    }
  };

  const seatColor = (remaining) =>
    remaining === 0 ? "text-[#C85A32]" : remaining <= 5 ? "text-[#D9822B]" : "text-[#4E7C59]";

  return (
    <section id="reservation" data-testid="booking-section" className="py-20 md:py-28 bg-[#201815]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mb-12">
          <p className="text-xs font-medium uppercase tracking-[0.25em] text-[#D9822B] mb-4">Réservation en ligne</p>
          <h2 className="font-display tracking-tight text-[#FBF8F3] text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Réservez votre <span className="italic text-[#D9822B]">créneau.</span>
          </h2>
          <p className="mt-4 max-w-xl text-sm text-[#FBF8F3]/60 leading-relaxed">
            Chaque horaire est limité à <span className="font-mono-brand text-[#FBF8F3]">14 personnes</span>, tous ateliers confondus, pour garantir un moment calme et accompagné. Confirmation immédiate par email.
          </p>
        </Reveal>

        <AnimatePresence mode="wait">
          {confirmation ? (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="rounded-3xl border border-[#4E7C59]/40 bg-[#4E7C59]/10 p-10 md:p-14 text-center"
              data-testid="booking-confirmation"
            >
              <CheckCircle2 size={48} className="mx-auto text-[#4E7C59] mb-6" />
              <h3 className="font-display text-3xl text-[#FBF8F3] mb-3">À très vite, {confirmation.name.split(" ")[0]} !</h3>
              <p className="text-[#FBF8F3]/70 max-w-md mx-auto text-sm leading-relaxed">
                Votre <strong className="text-[#FBF8F3]">{confirmation.workshop_name}</strong> est réservé pour le{" "}
                <strong className="text-[#FBF8F3]">{fmtFull.format(new Date(confirmation.date + "T12:00:00"))} à {confirmation.time}</strong>{" "}
                ({confirmation.seats} place{confirmation.seats > 1 ? "s" : ""}). Un email de confirmation vient de partir vers {confirmation.email}.
              </p>
              <p className="mt-8 inline-block rounded-2xl border border-dashed border-[#D9822B]/50 bg-[#201815] px-8 py-4">
                <span className="block text-xs uppercase tracking-[0.25em] text-[#FBF8F3]/50 mb-1">Code de réservation</span>
                <span data-testid="booking-code" className="font-mono-brand text-2xl text-[#D9822B]">{confirmation.code}</span>
              </p>
              <div className="mt-8 space-y-3">
                <a href="/annulation" data-testid="booking-cancel-link" className="block text-sm text-[#FBF8F3]/60 underline underline-offset-4 hover:text-[#D9822B] transition-colors">
                  Un imprévu ? Annulez en ligne avec votre code
                </a>
                <button
                  data-testid="booking-new-btn"
                  onClick={() => { setConfirmation(null); setSelectedSession(null); setForm({ name: "", email: "", phone: "", message: "", referral_code: "" }); }}
                  className="text-sm text-[#FBF8F3]/60 underline underline-offset-4 hover:text-[#D9822B] transition-colors"
                >
                  Faire une autre réservation
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-7 min-w-0 space-y-8">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#FBF8F3]/50 mb-4">1 — Choisissez une date</p>
                  <div data-testid="booking-dates" className="flex gap-2.5 overflow-x-auto pb-3 -mx-1 px-1">
                    {availability.map((d) => {
                      const day = new Date(d.date + "T12:00:00");
                      const active = selectedDate === d.date;
                      const full = d.remaining === 0;
                      return (
                        <button
                          key={d.date}
                          data-testid={`booking-date-${d.date}`}
                          disabled={full}
                          onClick={() => setSelectedDate(d.date)}
                          className={`shrink-0 w-[4.6rem] rounded-2xl border px-2 py-3 text-center transition-all duration-300 ${
                            active
                              ? "border-[#D9822B] bg-[#D9822B] text-[#201815]"
                              : full
                                ? "border-white/5 bg-white/[0.02] text-[#FBF8F3]/25 cursor-not-allowed"
                                : "border-white/10 bg-white/[0.04] text-[#FBF8F3] hover:border-[#D9822B]/60"
                          }`}
                        >
                          <span className="block text-[10px] uppercase tracking-wider opacity-70">{fmtDay.format(day)}</span>
                          <span className="block font-display text-xl leading-tight">{day.getDate()}</span>
                          <span className="block text-[10px] opacity-70">{fmtDate.format(day).split(" ")[1]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {workshopOptions.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-[#FBF8F3]/50 mb-4">2 — Choisissez votre atelier</p>
                    <div className="relative sm:max-w-sm">
                      <select
                        data-testid="booking-workshop-select"
                        value={workshopId}
                        onChange={(e) => { setWorkshopId(e.target.value); setSelectedSession(null); }}
                        className="w-full appearance-none cursor-pointer rounded-xl border border-white/10 bg-[#201815] px-4 py-3 pr-11 text-sm text-[#FBF8F3] transition-colors focus:border-[#D9822B] focus:outline-none"
                      >
                        {workshopOptions.map((w) => (
                          <option key={w.id} value={w.id} data-testid={`booking-workshop-option-${w.id}`} className="bg-[#201815] text-[#FBF8F3]">
                            {w.name}
                          </option>
                        ))}
                      </select>
                      <ChevronDown size={16} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#FBF8F3]/50" />
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#FBF8F3]/50 mb-4">3 — Choisissez un horaire</p>
                  {loadingSlots ? (
                    <div className="flex items-center gap-2 text-[#FBF8F3]/50 text-sm"><Loader2 size={15} className="animate-spin" /> Chargement des créneaux…</div>
                  ) : (
                    <div data-testid="booking-slots" className="grid sm:grid-cols-3 gap-3">
                      {visibleSessions.map((s) => {
                        const active = selectedSession?.id === s.id;
                        const remaining = s.slot_remaining ?? s.remaining;
                        const full = remaining === 0;
                        return (
                          <button
                            key={s.id}
                            data-testid={`booking-slot-${s.time.replace(":", "")}`}
                            disabled={full}
                            onClick={() => setSelectedSession(s)}
                            className={`rounded-2xl border p-4 text-left transition-all duration-300 ${
                              active
                                ? "border-[#D9822B] bg-[#D9822B]/15"
                                : full
                                  ? "border-white/5 bg-white/[0.02] cursor-not-allowed"
                                  : "border-white/10 bg-white/[0.04] hover:border-[#D9822B]/60"
                            }`}
                          >
                            <span className={`block font-display text-2xl ${full ? "text-[#FBF8F3]/25" : "text-[#FBF8F3]"}`}>{s.time}</span>
                            <span className={`block text-xs mt-1 font-mono-brand ${full ? "text-[#C85A32]" : seatColor(remaining)}`}>
                              {full ? "Complet" : `${remaining} place${remaining > 1 ? "s" : ""} / ${s.slot_capacity ?? s.capacity}`}
                            </span>
                          </button>
                        );
                      })}
                      {!visibleSessions.length && !loadingSlots && (
                        <p className="text-sm text-[#FBF8F3]/50 sm:col-span-3">Aucun créneau ce jour-là. Essayez une autre date.</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="lg:col-span-5 min-w-0">
                <form
                  data-testid="booking-form"
                  onSubmit={submit}
                  className="rounded-3xl border border-white/10 bg-white/[0.04] p-7 md:p-8 lg:sticky lg:top-28"
                >
                  <p className="text-xs uppercase tracking-[0.25em] text-[#FBF8F3]/50 mb-5">4 — Vos coordonnées</p>

                  <div className="mb-5 flex items-center justify-between rounded-2xl border border-white/10 bg-[#201815] px-5 py-4">
                    <div>
                      <p className="text-sm text-[#FBF8F3] font-medium">Participants</p>
                      <p className="text-xs text-[#FBF8F3]/45">
                        {selectedSession ? `${selectedSession.date.split("-").reverse().join("/")} à ${selectedSession.time} · ${selectedSession.workshop_name}` : "Sélectionnez un horaire"}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button type="button" data-testid="seats-minus" onClick={() => setSeats((s) => Math.max(1, s - 1))} className="h-8 w-8 rounded-full border border-white/15 text-[#FBF8F3] flex items-center justify-center hover:border-[#D9822B] transition-colors">
                        <Minus size={13} />
                      </button>
                      <span data-testid="seats-count" className="font-mono-brand text-lg text-[#FBF8F3] w-6 text-center">{seats}</span>
                      <button type="button" data-testid="seats-plus" onClick={() => setSeats((s) => Math.min(maxSeats, s + 1))} className="h-8 w-8 rounded-full border border-white/15 text-[#FBF8F3] flex items-center justify-center hover:border-[#D9822B] transition-colors">
                        <Plus size={13} />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3.5">
                    <input
                      data-testid="booking-name-input"
                      required
                      minLength={2}
                      placeholder="Nom & prénom"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full rounded-xl border border-white/10 bg-[#201815] px-4 py-3 text-sm text-[#FBF8F3] placeholder:text-[#FBF8F3]/30 focus:border-[#D9822B] focus:outline-none transition-colors"
                    />
                    <input
                      data-testid="booking-email-input"
                      required
                      type="email"
                      placeholder="Email (confirmation envoyée ici)"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full rounded-xl border border-white/10 bg-[#201815] px-4 py-3 text-sm text-[#FBF8F3] placeholder:text-[#FBF8F3]/30 focus:border-[#D9822B] focus:outline-none transition-colors"
                    />
                    <input
                      data-testid="booking-phone-input"
                      required
                      minLength={6}
                      placeholder="Téléphone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full rounded-xl border border-white/10 bg-[#201815] px-4 py-3 text-sm text-[#FBF8F3] placeholder:text-[#FBF8F3]/30 focus:border-[#D9822B] focus:outline-none transition-colors"
                    />
                    <textarea
                      data-testid="booking-message-input"
                      rows={2}
                      placeholder="Un mot pour nous ? (anniversaire, EVJF, allergies…)"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="w-full rounded-xl border border-white/10 bg-[#201815] px-4 py-3 text-sm text-[#FBF8F3] placeholder:text-[#FBF8F3]/30 focus:border-[#D9822B] focus:outline-none transition-colors resize-none"
                    />
                    <input
                      data-testid="booking-referral-input"
                      placeholder={`Code parrain (optionnel) — ${content?.referral_discount || "5€"} offerts pour vous deux`}
                      value={form.referral_code}
                      onChange={(e) => setForm({ ...form, referral_code: e.target.value })}
                      className="w-full rounded-xl border border-white/10 bg-[#201815] px-4 py-3 text-sm text-[#FBF8F3] placeholder:text-[#FBF8F3]/30 focus:border-[#D9822B] focus:outline-none transition-colors"
                    />
                  </div>

                  {error && (
                    <p data-testid="booking-error" className="mt-4 rounded-xl border border-[#C85A32]/40 bg-[#C85A32]/10 px-4 py-3 text-sm text-[#E89B7E]">
                      {error}
                    </p>
                  )}

                  <button
                    data-testid="booking-submit-btn"
                    type="submit"
                    disabled={!selectedSession || submitting}
                    className="mt-6 w-full rounded-full bg-[#C85A32] py-4 text-sm font-medium text-[#FBF8F3] transition-all duration-300 hover:bg-[#A84623] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {submitting && <Loader2 size={15} className="animate-spin" />}
                    {selectedSession ? `Confirmer — ${seats} place${seats > 1 ? "s" : ""} le ${selectedSession.date.split("-").reverse().join("/")} à ${selectedSession.time}` : "Sélectionnez un horaire"}
                  </button>
                  <p className="mt-3 text-center text-[11px] text-[#FBF8F3]/35">Règlement sur place — pièce choisie le jour J.</p>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
